import { useState } from "react";
import { Plus, X, Trash2, HelpCircle } from "lucide-react";
import { Button } from "../../primitives/Button";
import { Select } from "../../primitives/Select";
import { Input } from "../../primitives/Input";
import { type StepConditions, type StepConditionRule } from "../../../api/processes";
import { cn } from "../../utils/cn";

type StepConditionsBuilderProps = {
  conditions: StepConditions | null;
  onChange: (conditions: StepConditions) => void;
  availableSteps?: Array<{ id: string; step_key?: string | null; title: string }>;
  availableActivities?: Array<{ id: string; title: string }>;
};

// MVP condition types only (as per architecture decision)
const CONDITION_TYPES = [
  { value: "payment_ratio_reached", label: "Ratio de paiement atteint" },
  { value: "invoice_status", label: "Statut de la facture" },
  { value: "deliverable_status", label: "Statut d'un livrable" },
  { value: "step_completed", label: "Étape précédente complétée" },
  { value: "activity_completed", label: "Activité complétée" },
  { value: "manual_approval", label: "Approbation manuelle" },
  { value: "date_reached", label: "Date atteinte" },
  // task_status removed from MVP (will be added later)
];

export function StepConditionsBuilder({
  conditions,
  onChange,
  availableSteps = [],
  availableActivities = [],
}: StepConditionsBuilderProps) {
  const [mode, setMode] = useState<"structured" | "advanced">(conditions?.mode || "structured");
  const [allRules, setAllRules] = useState<StepConditionRule[]>(conditions?.all || []);
  const [anyRules, setAnyRules] = useState<StepConditionRule[]>(conditions?.any || []);

  const updateConditions = (newMode: typeof mode, newAll: typeof allRules, newAny: typeof anyRules) => {
    onChange({
      mode: newMode,
      all: newAll,
      any: newAny,
      advanced: newMode === "advanced" ? conditions?.advanced || {} : undefined,
    });
  };

  const addRule = (target: "all" | "any") => {
    const newRule: StepConditionRule = {
      type: "payment_ratio_reached",
      op: ">=",
      value: "",
    };
    if (target === "all") {
      const newAll = [...allRules, newRule];
      setAllRules(newAll);
      updateConditions(mode, newAll, anyRules);
    } else {
      const newAny = [...anyRules, newRule];
      setAnyRules(newAny);
      updateConditions(mode, allRules, newAny);
    }
  };

  const removeRule = (target: "all" | "any", index: number) => {
    if (target === "all") {
      const newAll = allRules.filter((_, i) => i !== index);
      setAllRules(newAll);
      updateConditions(mode, newAll, anyRules);
    } else {
      const newAny = anyRules.filter((_, i) => i !== index);
      setAnyRules(newAny);
      updateConditions(mode, allRules, newAny);
    }
  };

  const updateRule = (target: "all" | "any", index: number, updates: Partial<StepConditionRule>) => {
    if (target === "all") {
      const newAll = allRules.map((rule, i) => (i === index ? { ...rule, ...updates } : rule));
      setAllRules(newAll);
      updateConditions(mode, newAll, anyRules);
    } else {
      const newAny = anyRules.map((rule, i) => (i === index ? { ...rule, ...updates } : rule));
      setAnyRules(newAny);
      updateConditions(mode, allRules, newAny);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Mode:</span>
        <Select
          label=""
          hideLabel={true}
          value={mode === "structured" ? "Structuré" : "Avancé"}
          onChange={(value) => {
            const newMode = value === "Structuré" ? "structured" : "advanced";
            setMode(newMode);
            updateConditions(newMode, allRules, anyRules);
          }}
          options={["Structuré", "Avancé"]}
        />
      </div>

      {mode === "structured" ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Logique des conditions</label>
            <Select
              label=""
              hideLabel={true}
              value={conditions?.any?.length ? "Au moins une condition doit être vraie (OU)" : "Toutes les conditions doivent être vraies (ET)"}
              onChange={(value) => {
                const useAnd = value === "Toutes les conditions doivent être vraies (ET)";
                const currentRules = conditions?.any?.length ? anyRules : allRules;
                onChange({
                  mode: "structured",
                  all: useAnd ? currentRules : [],
                  any: useAnd ? [] : currentRules,
                });
                setAllRules(useAnd ? currentRules : []);
                setAnyRules(useAnd ? [] : currentRules);
              }}
              options={[
                "Toutes les conditions doivent être vraies (ET)",
                "Au moins une condition doit être vraie (OU)",
              ]}
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">
                {conditions?.any?.length ? "Au moins une de ces conditions doit être vraie." : "Toutes ces conditions doivent être vraies."}
              </span>
              <Button
                label="Ajouter une condition"
                intent="default"
                size="sm"
                onClick={() => addRule(conditions?.any?.length ? "any" : "all")}
              />
            </div>
            <div className="space-y-2">
              {(conditions?.any?.length ? anyRules : allRules).map((rule, idx) => (
                <ConditionRuleEditor
                  key={idx}
                  rule={rule}
                  onChange={(updates) => updateRule(conditions?.any?.length ? "any" : "all", idx, updates)}
                  onRemove={() => removeRule(conditions?.any?.length ? "any" : "all", idx)}
                  availableSteps={availableSteps}
                  availableActivities={availableActivities}
                />
              ))}
              {(conditions?.any?.length ? anyRules : allRules).length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-2 border border-dashed border-border rounded">
                  Aucune condition
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 border border-border rounded bg-surface-subtle">
          <p className="text-sm text-muted-foreground">
            Mode avancé: utilisez JSON pour définir des conditions complexes.
          </p>
          <textarea
            className="mt-2 w-full h-32 p-2 border border-border rounded font-mono text-xs"
            value={JSON.stringify(conditions?.advanced || {}, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                onChange({
                  mode: "advanced",
                  all: [],
                  any: [],
                  advanced: parsed,
                });
              } catch {
                // Invalid JSON, ignore
              }
            }}
            placeholder='{"custom": "condition"}'
          />
        </div>
      )}
    </div>
  );
}

function ConditionRuleEditor({
  rule,
  onChange,
  onRemove,
  availableSteps = [],
  availableActivities = [],
}: {
  rule: StepConditionRule;
  onChange: (updates: Partial<StepConditionRule>) => void;
  onRemove: () => void;
  availableSteps?: Array<{ id: string; step_key?: string | null; title: string; order_index: number }>;
  availableActivities?: Array<{ id: string; activity_key?: string | null; title: string }>;
}) {
  const getConditionHelp = (type: string) => {
    const helpTexts: Record<string, string> = {
      payment_ratio_reached: "Ratio de paiement de la facture (ex: 0.30 = 30%)",
      invoice_status: "Statut de la facture (draft, sent, paid, partially_paid)",
      deliverable_status: "Statut d'un livrable spécifique (pending, ready, delivered, accepted, rejected)",
      step_completed: "Vérifie si une étape précédente est complétée",
      activity_completed: "Vérifie si une activité est complétée",
      manual_approval: "Vérifie si une approbation manuelle a été donnée",
      date_reached: "Vérifie si une date spécifique est atteinte (format ISO: YYYY-MM-DDTHH:mm:ssZ)",
    };
    return helpTexts[type] || "";
  };

  const needsStepKey = rule.type === "step_completed";
  const needsActivityKey = rule.type === "activity_completed";
  const needsDeliverableCode = rule.type === "deliverable_status";
  const needsDate = rule.type === "date_reached";
  const needsBoolean = rule.type === "manual_approval";
  const needsNumber = rule.type === "payment_ratio_reached";
  const needsString = rule.type === "invoice_status";

  return (
    <div className="p-3 border border-border rounded bg-surface space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Select
              label=""
              hideLabel={true}
              value={CONDITION_TYPES.find((t) => t.value === rule.type)?.label || rule.type}
              onChange={(value) => {
                const type = CONDITION_TYPES.find((t) => t.label === value)?.value || rule.type;
                // Reset dependent fields when type changes
                onChange({
                  type,
                  step_key: type === "step_completed" ? rule.step_key : undefined,
                  activity_key: type === "activity_completed" ? rule.activity_key : undefined,
                  deliverable_code: type === "deliverable_status" ? rule.deliverable_code : undefined,
                });
              }}
              options={CONDITION_TYPES.map((t) => t.label)}
            />
            <div className="relative group">
              <HelpCircle size={16} className="text-muted-foreground cursor-help" />
              <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-10 w-64 p-2 rounded-md border border-border bg-surface text-xs text-text shadow-sm">
                {getConditionHelp(rule.type)}
              </div>
            </div>
          </div>
        </div>
        <Button
          label=""
          intent="ghost"
          size="sm"
          onClick={onRemove}
          icon={<Trash2 size={14} />}
        />
      </div>

      {needsStepKey && (
        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">
            Étape (step_key)
          </label>
          <Select
            label=""
            hideLabel={true}
            value={
              availableSteps.find((s) => s.step_key === rule.step_key)?.step_key || rule.step_key || ""
            }
            onChange={(value) => {
              const step = availableSteps.find((s) => s.step_key === value || s.title === value);
              onChange({ step_key: step?.step_key || value });
            }}
            options={availableSteps.map((s) => s.step_key || `${s.order_index}. ${s.title}`)}
          />
        </div>
      )}

      {needsActivityKey && (
        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">
            Activité (activity_key)
          </label>
          <Select
            label=""
            hideLabel={true}
            value={
              availableActivities.find((a) => a.activity_key === rule.activity_key)?.activity_key ||
              rule.activity_key ||
              ""
            }
            onChange={(value) => {
              const activity = availableActivities.find(
                (a) => a.activity_key === value || a.title === value
              );
              onChange({ activity_key: activity?.activity_key || value });
            }}
            options={availableActivities.map((a) => a.activity_key || a.title)}
          />
        </div>
      )}

      {needsDeliverableCode && (
        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">
            Code du livrable
          </label>
          <Input
            value={rule.deliverable_code || ""}
            onChange={(e) => onChange({ deliverable_code: e.target.value })}
            placeholder="Ex: mission_validation"
            className="w-full"
          />
        </div>
      )}

      {needsDate && (
        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">Date</label>
          <Input
            type="datetime-local"
            value={
              rule.value && typeof rule.value === "string"
                ? new Date(rule.value).toISOString().slice(0, 16)
                : ""
            }
            onChange={(e) => {
              const dateValue = e.target.value ? new Date(e.target.value).toISOString() : "";
              onChange({ value: dateValue });
            }}
            className="w-full"
          />
        </div>
      )}

      {needsBoolean && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id={`manual_approval_${rule.type}`}
            checked={rule.value === true}
            onChange={(e) => onChange({ value: e.target.checked })}
            className="rounded"
          />
          <label htmlFor={`manual_approval_${rule.type}`} className="text-sm">
            Approbation manuelle requise
          </label>
        </div>
      )}

      {needsNumber && (
        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">
            Ratio (0.0 - 1.0)
          </label>
          <Input
            type="number"
            min="0"
            max="1"
            step="0.01"
            value={typeof rule.value === "number" ? rule.value : rule.value || ""}
            onChange={(e) => {
              const numValue = parseFloat(e.target.value);
              onChange({ value: isNaN(numValue) ? 0 : numValue });
            }}
            placeholder="Ex: 0.30 pour 30%"
            className="w-full"
          />
        </div>
      )}

      {needsString && (
        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">Statut</label>
          <Select
            label=""
            hideLabel={true}
            value={String(rule.value || "")}
            onChange={(value) => onChange({ value })}
            options={["draft", "sent", "paid", "partially_paid"]}
          />
        </div>
      )}

      {!needsStepKey &&
        !needsActivityKey &&
        !needsDeliverableCode &&
        !needsDate &&
        !needsBoolean &&
        !needsNumber &&
        !needsString && (
          <div>
            <label className="block text-xs font-medium mb-1 text-muted-foreground">Valeur</label>
            <Input
              value={String(rule.value || "")}
              onChange={(e) => {
                const value = e.target.value;
                const numValue = Number(value);
                onChange({ value: isNaN(numValue) ? value : numValue });
              }}
              placeholder="Valeur"
              className="w-full"
            />
          </div>
        )}
    </div>
  );
}
